"use client";

import type { MutableRefObject } from "react";
import type {
  HomePageControllerLocal,
  HomePageInteractionState,
  OpenTeamEditorInput,
  StartPlanCheckoutFlowInput,
} from "./domain/home-page/types";
import { useHomePageRuntime } from "./useHomePageRuntime";
import { useHomePageGalleryHandlers } from "./useHomePageGalleryHandlers";
import { useHomePageHeroHandlers } from "./useHomePageHeroHandlers";
import { useHomePageNewTeamHandlers } from "./useHomePageNewTeamHandlers";
import { useHomePagePreflightHandlers } from "./useHomePagePreflightHandlers";
import { useHomePagePricingHandlers } from "./useHomePagePricingHandlers";
import { useHomePageTeamDetailsModalHandlers } from "./useHomePageTeamDetailsModalHandlers";

type HomePageRuntimeLocal = ReturnType<typeof useHomePageRuntime>;

type HomePageControllerHandlersLocal = Pick<
  HomePageControllerLocal,
  | "handleAllTeamsClick"
  | "handleAuthorizeClick"
  | "handleBuildYourOwnTeam"
  | "handleCloseAuthModal"
  | "handleClosePricingModal"
  | "handleCloseTeamsModal"
  | "handleHeroTeamDeploy"
  | "handleHeroTeamDetailsOpen"
  | "handlePricingClick"
  | "handlePricingPlanAccess"
  | "handleQuickStart"
  | "handleReplicateTeam"
  | "handleStartNewSession"
  | "handleTeamsModalLoginRequest"
  | "openTeamDetails"
> & {
  openDeployForTeam: (teamId: string) => void;
  openTeamEditor: (input: OpenTeamEditorInput) => void;
  startPlanCheckoutFlow: (input: StartPlanCheckoutFlowInput) => void;
};

interface UseHomePageControllerHandlersInput {
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  runtime: HomePageRuntimeLocal;
}

export function useHomePageControllerHandlers({
  latestInteractionState,
  runtime,
}: UseHomePageControllerHandlersInput): HomePageControllerHandlersLocal {
  const pricingHandlers = useHomePagePricingHandlers({
    hasActiveSubscription: runtime.hasActiveSubscription,
    isAuthenticated: runtime.isAuthenticated,
    modalState: runtime.modalState,
    openUserMenu: runtime.openUserMenu,
    startCheckout: runtime.startCheckout,
  });
  const galleryHandlers = useHomePageGalleryHandlers({
    latestInteractionState,
    modalDebugLogger: runtime.modalDebugLogger,
    modalState: runtime.modalState,
  });
  const preflightHandlers = useHomePagePreflightHandlers({
    latestInteractionState,
    modalDebugLogger: runtime.modalDebugLogger,
    modalState: runtime.modalState,
  });
  const newTeamHandlers = useHomePageNewTeamHandlers({
    latestInteractionState,
    modalDebugLogger: runtime.modalDebugLogger,
    modalState: runtime.modalState,
  });
  const teamDetailsHandlers = useHomePageTeamDetailsModalHandlers({
    clickInvestigationLog: runtime.clickInvestigationLog,
    latestInteractionState,
    modalDebugLogger: runtime.modalDebugLogger,
  });
  const heroHandlers = useHomePageHeroHandlers({
    clickInvestigationLog: runtime.clickInvestigationLog,
    latestInteractionState,
    modalDebugLogger: runtime.modalDebugLogger,
    openDeployForTeam: preflightHandlers.openDeployForTeam,
    openTeamDetails: teamDetailsHandlers.openTeamDetails,
  });

  return {
    handleAllTeamsClick: galleryHandlers.handleAllTeamsClick,
    handleAuthorizeClick: pricingHandlers.handleAuthorizeClick,
    handleBuildYourOwnTeam: newTeamHandlers.handleBuildYourOwnTeam,
    handleCloseAuthModal: pricingHandlers.handleCloseAuthModal,
    handleClosePricingModal: pricingHandlers.handleClosePricingModal,
    handleCloseTeamsModal: galleryHandlers.handleCloseTeamsModal,
    handleHeroTeamDeploy: heroHandlers.handleHeroTeamDeploy,
    handleHeroTeamDetailsOpen: heroHandlers.handleHeroTeamDetailsOpen,
    handlePricingClick: pricingHandlers.handlePricingClick,
    handlePricingPlanAccess: pricingHandlers.handlePricingPlanAccess,
    // handleQuickStart is absent this round: the entrance-wiring round adds
    // the Quick Start handler hook and maps it here; until then the landing's
    // Quick Start seat renders muted.
    handleReplicateTeam: preflightHandlers.handleReplicateTeam,
    handleStartNewSession: preflightHandlers.handleStartNewSession,
    handleTeamsModalLoginRequest: galleryHandlers.handleTeamsModalLoginRequest,
    openDeployForTeam: preflightHandlers.openDeployForTeam,
    openTeamDetails: teamDetailsHandlers.openTeamDetails,
    openTeamEditor: teamDetailsHandlers.openTeamEditor,
    startPlanCheckoutFlow: pricingHandlers.startPlanCheckoutFlow,
  };
}
